
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, 
  Check,
  X,
  CheckSquare
} from 'lucide-react';
import { useNoteChecklistItems } from '@/hooks/useNoteChecklistItems';
import { ChecklistItem } from '@/hooks/useChecklistItems';
import { NoteChecklistComponent } from './NoteChecklistComponent';
import { useBoardContext } from '@/contexts/BoardContext';
import { supabase } from '@/integrations/supabase/client';

interface NoteChecklistManagerProps {
  noteId: string;
  noteVisibility?: 'private' | 'board_shared';
  noteBoardId?: string | null;
  onChecklistChange?: (hasItems: boolean) => void;
}

export const NoteChecklistManager = ({ 
  noteId, 
  noteVisibility = 'private',
  noteBoardId = null,
  onChecklistChange 
}: NoteChecklistManagerProps) => {
  const { profiles } = useBoardContext();
  const [boardProfiles, setBoardProfiles] = useState<any[]>([]);
  const { 
    checklistItems, 
    createChecklistItem, 
    updateChecklistItem, 
    deleteChecklistItem,
    createChecklistWithName,
    isCreating,
    isUpdating,
    isDeleting,
    isLoadingVirtualCard,
    virtualCardId
  } = useNoteChecklistItems(noteId);

  const [newChecklistName, setNewChecklistName] = useState('');
  const [isAddingChecklist, setIsAddingChecklist] = useState(false);
  const [checklistNames, setChecklistNames] = useState<Record<string, string>>({});

  const isNotePrivate = noteVisibility === 'private';

  // Fetch board profiles if this is a shared note
  useEffect(() => {
    const fetchBoardProfiles = async () => {
      if (!isNotePrivate && noteBoardId) {
        try {
          const { data, error } = await supabase
            .from('board_members')
            .select('user_id, profiles(id, full_name, username)')
            .eq('board_id', noteBoardId);

          if (error) throw error;
          
          const profilesData = data?.map(member => member.profiles).filter(Boolean) || [];
          setBoardProfiles(profilesData);
        } catch (error) {
          console.error('Error fetching board profiles:', error);
          setBoardProfiles([]);
        }
      } else {
        setBoardProfiles([]);
      }
    };

    fetchBoardProfiles();
  }, [isNotePrivate, noteBoardId]);

  // Group items by checklist_id and extract checklist names
  const groupedChecklists = checklistItems.reduce((acc, item) => {
    if (!acc[item.checklist_id]) {
      acc[item.checklist_id] = [];
    }
    acc[item.checklist_id].push(item);
    
    // Extract checklist name from first item's text if it has the special prefix
    if (!checklistNames[item.checklist_id] && item.text.startsWith('__CHECKLIST_NAME__')) {
      const nameMatch = item.text.match(/^__CHECKLIST_NAME__(.+?)__ADD_FIRST_TASK__$/);
      if (nameMatch) {
        setChecklistNames(prev => ({
          ...prev,
          [item.checklist_id]: nameMatch[1]
        }));
      }
    }
    
    return acc;
  }, {} as Record<string, ChecklistItem[]>);

  const addChecklist = async () => {
    if (newChecklistName.trim()) {
      await createChecklistWithName(newChecklistName.trim());
      setNewChecklistName('');
      setIsAddingChecklist(false);
    }
  };

  const deleteChecklist = (checklistId: string) => {
    const items = groupedChecklists[checklistId] || [];
    items.forEach(item => deleteChecklistItem(item.id));
    // Clean up checklist name
    setChecklistNames(prev => {
      const newNames = { ...prev };
      delete newNames[checklistId];
      return newNames;
    });
  };

  const renameChecklist = async (checklistId: string, newName: string) => {
    // Update the first item's text to store the new checklist name
    const items = groupedChecklists[checklistId];
    if (items.length > 0) {
      const firstItem = items[0];
      
      // If it's a special naming item, update it with the new name
      if (firstItem.text.startsWith('__CHECKLIST_NAME__')) {
        await updateChecklistItem({
          itemId: firstItem.id,
          updates: {
            text: `__CHECKLIST_NAME__${newName}__ADD_FIRST_TASK__`
          }
        });
      }
      
      setChecklistNames(prev => ({
        ...prev,
        [checklistId]: newName
      }));
    }
  };

  const getChecklistProgress = (items: ChecklistItem[]) => {
    if (items.length === 0) return 0;
    const completed = items.filter(item => item.completed).length;
    return Math.round((completed / items.length) * 100);
  };

  const getChecklistName = (checklistId: string) => {
    return checklistNames[checklistId] || `Checklist ${checklistId.slice(-4)}`;
  };

  // Use board profiles for shared notes, empty array for private notes
  const availableProfiles = isNotePrivate ? [] : (boardProfiles.length > 0 ? boardProfiles : profiles || []);

  // Notify parent of checklist status changes
  useEffect(() => {
    onChecklistChange?.(checklistItems.length > 0);
  }, [checklistItems.length, onChecklistChange]);

  if (isLoadingVirtualCard) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="text-sm text-muted-foreground">Loading checklists...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckSquare className="h-4 w-4" />
          <h4 className="font-medium">Checklists</h4>
          {checklistItems.length > 0 && (
            <Badge variant="secondary">{checklistItems.length} items</Badge>
          )}
          {isNotePrivate && (
            <Badge variant="outline" className="text-xs">
              Private
            </Badge>
          )}
        </div>
        {!isAddingChecklist && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsAddingChecklist(true)}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Checklist
          </Button>
        )}
      </div>

      {isAddingChecklist && (
        <div className="flex gap-2">
          <Input
            placeholder="Checklist name"
            value={newChecklistName}
            onChange={(e) => setNewChecklistName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addChecklist();
              if (e.key === 'Escape') {
                setIsAddingChecklist(false);
                setNewChecklistName('');
              }
            }}
            autoFocus
          />
          <Button type="button" size="sm" onClick={addChecklist} disabled={isCreating}>
            <Check className="h-4 w-4" />
          </Button>
          <Button 
            type="button" 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              setIsAddingChecklist(false);
              setNewChecklistName('');
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {Object.entries(groupedChecklists).map(([checklistId, items]) => (
        <NoteChecklistComponent
          key={checklistId}
          checklistId={checklistId}
          checklistName={getChecklistName(checklistId)}
          items={items}
          profiles={availableProfiles}
          isNotePrivate={isNotePrivate}
          onDelete={() => deleteChecklist(checklistId)}
          onCreateItem={createChecklistItem}
          onUpdateItem={updateChecklistItem}
          onDeleteItem={deleteChecklistItem}
          onRenameChecklist={(newName) => renameChecklist(checklistId, newName)}
          progress={getChecklistProgress(items)}
          isCreating={isCreating}
          isUpdating={isUpdating}
          isDeleting={isDeleting}
        />
      ))}

      {checklistItems.length === 0 && !isAddingChecklist && (
        <div className="text-center py-8 text-muted-foreground">
          <CheckSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No checklists yet</p>
          <p className="text-xs">Add a checklist to track tasks in this note</p>
        </div>
      )}
    </div>
  );
};
